import draftToHtml from 'draftjs-to-html';
import { ReactNode } from 'react';

import InlineSvg, { InlineSvgIds } from 'approot/shared/inline-svg/inline-svg';
import type { ResponsiveImageFragment } from '../../../shared/graphql/responsive-images/responsive-images.graphql.generated';
import ImageThumbnail, {
  ImageThumbnailStatic,
} from 'approot/shared/images/images';
import { JSONObject } from 'global/graphql/scalars.type';
import * as Styled from './component-parts.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import {
  PlayableComponentButton,
  PlayableComponentCover,
  PlayableComponentStaticButton,
  PlayableComponentButtonText,
} from './playable-component.style';
import { StimulusResourceType } from 'global/constants';
import { getStimulusLabel } from './logic/get-stimulus-label';
import ComponentTag from './component-tag';
import IconExpand from 'approot/shared/icons/icon-expand';

type Props = {
  iconId?: InlineSvgIds;
  image?: string;
  onClick: () => void;
  buttonText: string;
  isSmall?: boolean;
};

export const ComponentEmptyButton = ({
  iconId,
  image,
  onClick,
  buttonText,
  isSmall,
}: Props) => {
  const button = (
    <Styled.ComponentEmptyButton $isSmall={isSmall}>
      <div>
        {iconId && (
          <Styled.ComponentEmptyButtonIcon>
            <InlineSvg
              id={iconId}
              width={isSmall ? 48 : 64}
              height={isSmall ? 48 : 64}
            />
          </Styled.ComponentEmptyButtonIcon>
        )}
        {image && <Styled.ComponentEmptyButtonImage src={image} alt="" />}
        <SecondaryButton
          data-selector={`btn-${formatToLowerKebabCase(buttonText)}`}
          onClick={onClick}
        >
          {buttonText}
        </SecondaryButton>
      </div>
    </Styled.ComponentEmptyButton>
  );
  if (isSmall) {
    return button;
  }

  return (
    <Styled.ComponentEmptyButtonWrapper>
      {button}
    </Styled.ComponentEmptyButtonWrapper>
  );
};

export const ComponentLinkItem = ({
  primaryText,
  squareImage,
  link,
  textBadge,
  showDetails = true,
  onClick,
}: {
  primaryText: string;
  link: string | null;
  squareImage: ResponsiveImageFragment | null;
  textBadge?: string;
  showDetails?: boolean;
  onClick?: () => void;
}) => (
  <Styled.ComponentLinkItem
    href={link || ''}
    target="_blank"
    aria-label={`${primaryText} (opens in a new tab)`}
    data-testid="link-item"
    onClick={onClick}
  >
    <Styled.ComponentItemContent>
      <Styled.ComponentItemImage>
        <ImageThumbnail images={squareImage?.responsive || []} />
      </Styled.ComponentItemImage>
      {showDetails && (
        <Styled.ComponentItemCol>
          <Styled.ComponentItemText data-selector="name">
            {primaryText}
          </Styled.ComponentItemText>
          <Styled.ComponentBadgeWrapper>
            {textBadge && <TextBadge text={textBadge} />}
          </Styled.ComponentBadgeWrapper>
        </Styled.ComponentItemCol>
      )}
    </Styled.ComponentItemContent>
    <Styled.ComponentLinkIconWrapper>
      <IconExpand width={16} height={16} />
    </Styled.ComponentLinkIconWrapper>
  </Styled.ComponentLinkItem>
);

export const ComponentLinkItemPreview = ({
  primaryText,
  squareImage,
  textBadge,
  showDetails = true,
}: {
  primaryText: string;
  squareImage: ResponsiveImageFragment | null;
  textBadge?: string;
  showDetails?: boolean;
}) => (
  <Styled.ComponentLinkPreviewItem>
    <Styled.ComponentItemContent>
      <Styled.ComponentItemImage>
        <ImageThumbnailStatic
          imageUrl={squareImage?.responsive?.[0].urls[0].url || ''}
        />
      </Styled.ComponentItemImage>
      {showDetails && (
        <Styled.ComponentItemCol>
          <Styled.ComponentItemText data-selector="name">
            {primaryText}
          </Styled.ComponentItemText>
          <Styled.ComponentBadgeWrapper>
            {textBadge && <TextBadge text={textBadge} />}
          </Styled.ComponentBadgeWrapper>
        </Styled.ComponentItemCol>
      )}
    </Styled.ComponentItemContent>
  </Styled.ComponentLinkPreviewItem>
);

export const ComponentLinkItemPreviewNarrow = ({
  primaryText,
  squareImage,
  textBadge,
  showDetails = true,
}: {
  primaryText: string;
  squareImage: ResponsiveImageFragment | null;
  textBadge?: string;
  showDetails?: boolean;
}) => (
  <Styled.ComponentLinkPreviewItem>
    <Styled.ComponentItemContentNarrow>
      <Styled.ComponentItemImageNarrow>
        <ImageThumbnailStatic
          imageUrl={squareImage?.responsive?.[0].urls[0].url || ''}
        />
      </Styled.ComponentItemImageNarrow>
      {showDetails && (
        <Styled.ComponentItemCol>
          <Styled.ComponentItemText data-selector="name">
            {primaryText}
          </Styled.ComponentItemText>
          <Styled.ComponentBadgeWrapper>
            {textBadge && <TextBadge text={textBadge} />}
          </Styled.ComponentBadgeWrapper>
        </Styled.ComponentItemCol>
      )}
    </Styled.ComponentItemContentNarrow>
  </Styled.ComponentLinkPreviewItem>
);

export const ComponentLinkItemNarrow = ({
  primaryText,
  squareImage,
  link,
  textBadge,
  showDetails = true,
  onClick,
}: {
  primaryText: string;
  link: string | null;
  squareImage: ResponsiveImageFragment | null;
  textBadge?: string;
  showDetails?: boolean;
  onClick?: () => void;
}) => (
  <Styled.ComponentLinkItem
    href={link || ''}
    target="_blank"
    aria-label={`${primaryText} (opens in a new tab)`}
    data-testid="link-item"
    onClick={onClick}
  >
    <Styled.ComponentItemContentNarrow>
      <Styled.ComponentItemImageNarrow>
        <ImageThumbnail images={squareImage?.responsive || []} />
      </Styled.ComponentItemImageNarrow>
      {showDetails && (
        <Styled.ComponentItemCol>
          <Styled.ComponentItemText data-selector="name">
            {primaryText}
          </Styled.ComponentItemText>
          <Styled.ComponentBadgeWrapper>
            {textBadge && <TextBadge text={textBadge} />}
          </Styled.ComponentBadgeWrapper>
        </Styled.ComponentItemCol>
      )}
    </Styled.ComponentItemContentNarrow>
    <Styled.ComponentLinkIconWrapper>
      <IconExpand width={16} height={16} />
    </Styled.ComponentLinkIconWrapper>
  </Styled.ComponentLinkItem>
);

export const ComponentStartCover = ({
  type,
  resourceName,
  buttonLabel,
  backgroundImage,
  style,
  extraContent,
  onClick,
}: {
  type: StimulusResourceType;
  resourceName: string;
  buttonLabel: string;
  backgroundImage?: string | null;
  extraContent?: ReactNode;
  style?: {
    width: string;
    height: string;
  };
  onClick?: () => void;
}) => {
  const label = getStimulusLabel(type);
  return (
    <PlayableComponentCover
      $backgroundImg={backgroundImage}
      $fullBorderRadius
      $noPaddingTop
      style={style}
    >
      {onClick ? (
        <PlayableComponentButton
          type="button"
          data-testid="play-button"
          aria-label={`Click to ${buttonLabel} ${label}: ${resourceName}`}
          onClick={onClick}
          data-selector={`btn-${formatToLowerKebabCase(buttonLabel)}`}
        >
          <PlayableComponentButtonText>
            {buttonLabel}
          </PlayableComponentButtonText>
        </PlayableComponentButton>
      ) : (
        <PlayableComponentStaticButton>
          <PlayableComponentButtonText>
            {buttonLabel}
          </PlayableComponentButtonText>
        </PlayableComponentStaticButton>
      )}
      <ComponentTag iconId={type} name={label} />
      {extraContent}
    </PlayableComponentCover>
  );
};

export const TextBadge = ({ text }: { text: string }) => {
  return (
    <Styled.ComponentTextBadge>
      <span>{text}</span>
    </Styled.ComponentTextBadge>
  );
};

export const ComponentDetails = ({
  name,
  description,
  link,
  badgeName,
  badgeIconId,
}: {
  name: string;
  description: JSONObject;
  link?: {
    url: string;
    target?: string;
  };
  badgeName: string;
  badgeIconId: InlineSvgIds;
}) => {
  const contentMarkup = draftToHtml(description);
  return (
    <Styled.ComponentDisplayDetails>
      <Styled.ComponentDisplayName>
        {link ? (
          link.url.includes('http') ? (
            <Styled.ComponentDisplayAnchor href={link.url} target={link.target}>
              {name}
              <IconExpand width={16} height={16} />
            </Styled.ComponentDisplayAnchor>
          ) : (
            <Styled.ComponentDisplayLink to={link.url} target={link.target}>
              {name}
              <IconExpand width={16} height={16} />
            </Styled.ComponentDisplayLink>
          )
        ) : (
          name
        )}
      </Styled.ComponentDisplayName>
      <Styled.ComponentDisplayDescription
        dangerouslySetInnerHTML={{
          __html: contentMarkup,
        }}
      />
      <ComponentTag iconId={badgeIconId} name={badgeName} />
    </Styled.ComponentDisplayDetails>
  );
};
